import { usePage, router } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface SurveySidebarProps {
  survey: {
    id: number
    name: string
    status: 'draft' | 'active' | 'closed'
    anonymity?: 'anonymous' | 'named' | null
  }
}

const STATUS_BADGE: Record<string, string> = {
  draft: 'badge badge-warning badge-sm',
  active: 'badge badge-success badge-sm',
  closed: 'badge badge-error badge-sm',
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  closed: 'Closed',
}

function getMenuItems(surveyId: number, named: boolean, ap: (path: string) => string) {
  return [
    { id: 'overview', icon: 'lucide--layout-dashboard', label: 'Overview', url: ap(`/surveys/${surveyId}`) },
    // Respondent management only exists on named surveys.
    ...(named ? [{ id: 'respondents', icon: 'lucide--users', label: 'Respondents', url: ap(`/surveys/${surveyId}/respondents`) }] : []),
    { id: 'results', icon: 'lucide--bar-chart-3', label: 'Results', url: ap(`/surveys/${surveyId}/results`) },
  ]
}

function getSettingsItems(surveyId: number, named: boolean, ap: (path: string) => string) {
  return [
    { id: 'questions', icon: 'lucide--list', label: 'Questions', url: ap(`/surveys/${surveyId}/edit?tab=questions`) },
    { id: 'settings', icon: 'lucide--sliders-horizontal', label: 'Settings', url: ap(`/surveys/${surveyId}/edit?tab=settings`) },
    // Invitation/reminder templates only exist on named surveys.
    ...(named ? [{ id: 'emails', icon: 'lucide--mail', label: 'Email Templates', url: ap(`/surveys/${surveyId}/edit?tab=emails`) }] : []),
    { id: 'branding', icon: 'lucide--palette', label: 'Branding', url: ap(`/surveys/${surveyId}/edit?tab=branding`) },
  ]
}

export default function SurveySidebar({ survey }: SurveySidebarProps) {
  const { current_user } = usePage<{ props: SharedProps }>().props as unknown as SharedProps
  const ap = useAccountPath()
  const currentPath = window.location.pathname
  const currentSearch = window.location.search
  const menuItems = getMenuItems(survey.id, survey.anonymity === 'named', ap)
  const settingsItems = getSettingsItems(survey.id, survey.anonymity === 'named', ap)

  function isMenuActive(item: { id: string; url: string }) {
    return item.id === 'overview'
      ? currentPath === item.url
      : currentPath.startsWith(item.url)
  }

  function isSettingsActive(item: { id: string; url: string }) {
    if (!currentPath.startsWith(ap(`/surveys/${survey.id}/edit`))) return false
    const tab = new URLSearchParams(currentSearch).get('tab') || 'questions'
    return tab === item.id
  }

  function handleLogout() {
    router.delete('/logout')
  }

  const userInitial = current_user?.consultancy_name?.charAt(0).toUpperCase()
    || current_user?.email_address?.charAt(0).toUpperCase()
    || '?'

  const userDisplayName = current_user?.consultancy_name || current_user?.email_address?.split('@')[0] || 'User'

  return (
    <>
      <input
        type="checkbox"
        id="survey-sidebar-toggle"
        className="hidden peer"
      />

      {/* Mobile backdrop */}
      <label
        htmlFor="survey-sidebar-toggle"
        className="fixed inset-0 z-40 bg-base-content/20 hidden peer-checked:block lg:!hidden"
      />

      <aside className="sidebar-menu fixed z-50 h-screen w-64 flex flex-col -translate-x-full peer-checked:translate-x-0 transition-transform duration-300 lg:relative lg:translate-x-0 lg:z-10 border-r border-white/10">
        {/* Survey name + status */}
        <div className="border-b border-white/10 px-4 py-3 text-center">
          <h2 className="font-semibold text-base">{survey.name}</h2>
          <span className={`${STATUS_BADGE[survey.status]} mt-1 w-full`}>
            {STATUS_LABEL[survey.status]}
          </span>
          {survey.status === 'draft' && (
            <button
              onClick={() => router.post(ap(`/surveys/${survey.id}/publish`))}
              className="btn btn-primary btn-sm w-full mt-3"
            >
              <span className="iconify lucide--send size-4" />
              Publish survey
            </button>
          )}
        </div>

        {/* Menu items */}
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {menuItems.map((item) => (
            <a
              key={item.id}
              href={item.url}
              className={`menu-item ${isMenuActive(item) ? 'active' : ''}`}
            >
              <span className={`iconify ${item.icon} size-5`} />
              <span className="grow">{item.label}</span>
            </a>
          ))}

          {/* Settings section */}
          <div className="menu-label">Settings</div>
          {settingsItems.map((item) => (
            <a
              key={item.id}
              href={item.url}
              className={`menu-item ${isSettingsActive(item) ? 'active' : ''}`}
            >
              <span className={`iconify ${item.icon} size-5`} />
              <span className="grow">{item.label}</span>
            </a>
          ))}
        </nav>

        {/* Back to all surveys */}
        <div className="px-2 pb-2">
          <a href={ap('/surveys')} className="menu-item text-primary-content/60">
            <span className="iconify lucide--arrow-left size-4" />
            <span>All Surveys</span>
          </a>
        </div>

        {/* User profile dropdown */}
        <div className="border-t border-white/10 p-2">
          <div className="dropdown dropdown-top w-full">
            <div
              tabIndex={0}
              role="button"
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 cursor-pointer w-full"
            >
              {current_user?.logo_url ? (
                <img
                  src={current_user.logo_url}
                  alt="Logo"
                  className="size-9 rounded-full object-cover ring-2 ring-white/20"
                />
              ) : (
                <div className="avatar avatar-placeholder">
                  <div className="bg-white/20 text-primary-content rounded-full w-9">
                    <span className="text-sm">{userInitial}</span>
                  </div>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{userDisplayName}</p>
                {current_user?.consultancy_name && (
                  <p className="text-xs text-primary-content/60 truncate">{current_user.email_address}</p>
                )}
              </div>
              <span className="iconify lucide--chevrons-up-down size-4 text-primary-content/60" />
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 text-base-content rounded-box w-full p-2 shadow-lg border border-base-200 mb-2"
            >
              <li>
                <a href={ap('/settings')}>
                  <span className="iconify lucide--settings size-4" />
                  Account Settings
                </a>
              </li>
              <div className="divider my-1" />
              <li>
                <button onClick={handleLogout}>
                  <span className="iconify lucide--log-out size-4" />
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </>
  )
}
